import * as Yup from 'yup';
import { ContextService } from '../../../hoc/context/product/context';
import { EnrollmentContextService } from '../../../hoc/context/enrollment/enrollment-context';
import { ConsentIDs } from '../../../hoc/constants/service.constants';
import { dobValidation } from './validations-extenstions';
import { Message } from 'yup/lib/types';

const baseStrValidator = (params: {
	name: string;
	suffix: string;
	ctxt: ContextService;
	testName: string;
	max?: number;
	regex?: RegExp;
	then: {
		max?: number;
		regex?: RegExp;
		nullable?: boolean;
		required?: boolean;
	};
	otherwise: {
		max?: number;
		regex?: RegExp;
		nullable?: boolean;
		required?: boolean;
	};
}) => {
	const { name, suffix, ctxt, max, regex, testName, then, otherwise } = params;

	let result = Yup.string()
		.trim()
		.test(
			testName,
			ctxt.json.getField(name + suffix)?.errors?.required as Message,
			(value) => {
				return suffix ? true : !!value;
			}
		)
		.when('checkedSameAddress', {
			is: (val: boolean) => val === false || val === undefined,
			then: whenThenOtherwise({
				name,
				suffix,
				ctxt,
				max: then.max,
				regex: then.regex,
				nullable: then.nullable,
				required: then.required,
			}),
			otherwise: whenThenOtherwise({
				name,
				suffix,
				ctxt,
				max: otherwise.max,
				regex: otherwise.regex,
				nullable: otherwise.nullable,
				required: otherwise.required,
			}),
		});

	if (max) {
		result = result.max(
			max,
			ctxt.json.getField(name + suffix)?.errors?.maxLength
		);
	}
	if (regex) {
		result = result.matches(
			regex,
			ctxt.json.getField(name + suffix)?.errors?.mismatch
		);
	}

	return result;
};

const whenThenOtherwise = (params: {
	name: string;
	suffix: string;
	ctxt: ContextService;
	max?: number;
	regex?: RegExp;
	nullable?: boolean;
	required?: boolean;
}) => {
	const { name, suffix, ctxt, max, regex, nullable, required } = params;
	let validationResult = Yup.string();
	if (nullable) {
		validationResult = validationResult.nullable(false);
	}
	if (required) {
		validationResult = validationResult.required(
			ctxt.json.getField(name + suffix)?.errors?.required
		);
	}
	if (regex) {
		validationResult = validationResult.matches(
			regex,
			ctxt.json.getField(name + suffix)?.errors?.mismatch
		);
	}
	if (max) {
		validationResult = validationResult.max(
			max,
			ctxt.json.getField(name + suffix)?.errors?.maxLength
		);
	}

	return validationResult;
};

export const commonValidations = (
	ctxt: ContextService,
	enrollmentCtxt: EnrollmentContextService,
	suffix = ''
) => {
	const zipMaxLength = 5;
	const mailMaxLength = 50;
	const aptMaxLength = 50;
	const fieldRegex =
		/^(?!\s*$)(?!.*[-'.~¨`´]{2})(?=.*[a-zA-Z])[a-zA-Z-'.~¨`´ÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûü ]+$/;
	const mailRegex =
		/^(?!\s*$)(?!.*[-'.~¨`´+]{2})(?=.*[a-zA-Z0-9])[a-zA-Z0-9-'.~¨`´+ÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûü ]+$/;
	const validation: { [key: string]: Yup.AnySchema } = {};
	validation['firstname' + suffix] = Yup.string()
		.trim()
		.required(ctxt.json.getField('firstname' + suffix)?.errors?.required)
		.matches(
			fieldRegex,
			ctxt.json.getField('firstname' + suffix)?.errors?.mismatch
		);
	validation['lastname' + suffix] = Yup.string()
		.trim()
		.required(ctxt.json.getField('lastname' + suffix)?.errors?.required)
		.matches(
			fieldRegex,
			ctxt.json.getField('lastname' + suffix)?.errors?.mismatch
		);
	validation['dob'] = Yup.string()
		.required(ctxt.json.getField('dob')?.errors?.required)
		.test('dob-validation', '', function (value) {
			const validationResult = dobValidation(value, ctxt, enrollmentCtxt);
			return validationResult === true
				? validationResult
				: this.createError({
						message: validationResult,
				  });
		});
	validation['gender' + suffix] = Yup.string()
		.nullable()
		.required(ctxt.json.getField('gender' + suffix)?.errors?.required);
	if (ctxt.json.getField('medication')) {
		validation['medication'] = Yup.string()
			.nullable()
			.required(ctxt.json.getField('medication')?.errors?.required);
	}
	validation['mail' + suffix] = baseStrValidator({
		name: 'mail',
		suffix,
		ctxt,
		testName: 'mail-required',
		then: {
			max: mailMaxLength,
			regex: mailRegex,
			nullable: false,
			required: true,
		},
		otherwise: {
			max: mailMaxLength,
			regex: mailRegex,
			nullable: true,
			required: false,
		},
	});

	validation['apt' + suffix] = Yup.string().max(
		aptMaxLength,
		ctxt.json.getField('apt' + suffix)?.errors?.maxLength
	);
	validation['city' + suffix] = baseStrValidator({
		name: 'city',
		suffix,
		ctxt,
		testName: 'city-required',
		then: {
			regex: fieldRegex,
			nullable: false,
			required: true,
		},
		otherwise: {
			regex: fieldRegex,
			nullable: true,
			required: false,
		},
	});

	validation['state' + suffix] = baseStrValidator({
		name: 'state',
		suffix,
		ctxt,
		testName: 'state-required',
		then: {
			nullable: false,
			required: true,
		},
		otherwise: {
			nullable: true,
			required: false,
		},
	});

	validation['zip' + suffix] = baseStrValidator({
		name: 'zip',
		suffix,
		ctxt,
		testName: 'zip-required',
		max: zipMaxLength,
		regex: /^[0-9]{5}$/,
		then: {
			nullable: false,
			required: true,
		},
		otherwise: {
			nullable: true,
			required: false,
		},
	});

	validation['email'] = Yup.string()
		.trim()
		.nullable()
		.email(ctxt.json.getField('email')?.errors?.mismatch)
		.matches(
			/^((\s*)[a-zA-Z0-9._%+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})?$/,
			ctxt.json.getField('email')?.errors?.mismatch
		);

	validation['phone'] = Yup.string()
		.required(ctxt.json.getField('phone')?.errors?.required)
		.matches(
			/^\(?([2-9][0-8]\d)\)?([2-9]\d{2})(\d{4})$/,
			ctxt.json.getField('phone')?.errors?.mismatch
		);
	validation['mobile'] = Yup.string().test(
		'mobile-match',
		ctxt.json.getField('mobile')?.errors?.mismatch as Message,
		(value) => {
			return value
				? Yup.string()
						.matches(
							/^\(?([2-9][0-8]\d)\)?([2-9]\d{2})(\d{4})$/,
							ctxt.json.getField('mobile')?.errors?.mismatch
						)
						.nullable()
						.isValidSync(value)
				: Yup.string().nullable().isValidSync(value);
		}
	);
	validation['phoneusage'] = Yup.string()
		.required(ctxt.json.getField('phoneusage')?.errors?.required)
		.nullable();
	if (ctxt.json.consents[ConsentIDs.hipaa]) {
		validation['consenthipaa'] = Yup.boolean()
			.required(ctxt.json.getField('consenthipaa')?.errors?.required)
			.oneOf([true], ctxt.json.getField('consenthipaa')?.errors?.required);
	}
	if (ctxt.json.consents[ConsentIDs.tcpa]) {
		validation['consenttcpa'] = Yup.boolean();
		validation['bestTimeToConnect'] = Yup.string()
			.nullable()
			.when('consenttcpa', {
				is: (consenttcpa: boolean) => consenttcpa,
				then: Yup.string()
					.nullable()
					.required(ctxt.json.getField('bestTimeToConnect')?.errors?.required),
				otherwise: Yup.string().nullable(),
			});
	}
	return validation;
};
